import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { usePacientes } from '../hooks/usePacientes';
import { useAuth } from '../contexts/AuthContext';
import { ref, get } from '../services/phpDataAdapter';
import { database } from '../config/backend';
import { getAdherenceMetrics } from '../utils/adherenceMetrics';
import './Pages.css';

interface PatientAdherence {
  id: string;
  nome: string;
  email: string;
  adherence: number | null;
  lastUpdate?: string;
}

function getDateKey(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const Adherence: React.FC = () => {
  const { user } = useAuth();
  const { pacientes, loading: loadingPacientes } = usePacientes();
  const [patientAdherence, setPatientAdherence] = useState<PatientAdherence[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'adherence'>('adherence');

  useEffect(() => {
    async function loadAdherence() {
      if (!user?.uid || pacientes.length === 0) {
        if (!loadingPacientes) {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const todayKey = getDateKey();
        const adherencePromises = pacientes.map(async (paciente) => {
          try {
            const scoreSnap = await get(
              ref(database, `users/${paciente.id}/dietTracking/${todayKey}/dailyScore`)
            );
            const rawScore = scoreSnap.exists() ? Number(scoreSnap.val()) : null;
            const score = rawScore !== null && Number.isFinite(rawScore)
              ? Math.max(0, Math.min(100, rawScore))
              : null;
            
            // Tentar buscar data da última atualização
            let lastUpdate: string | undefined;
            try {
              const trackingSnap = await get(
                ref(database, `users/${paciente.id}/dietTracking/${todayKey}/lastUpdatedAt`)
              );
              if (trackingSnap.exists()) {
                lastUpdate = trackingSnap.val();
              }
            } catch {
              // Ignora erro
            }

            return {
              id: paciente.id,
              nome: paciente.nome,
              email: paciente.email || '',
              adherence: score,
              lastUpdate,
            };
          } catch {
            return {
              id: paciente.id,
              nome: paciente.nome,
              email: paciente.email || '',
              adherence: null,
            };
          }
        });

        const adherenceData = await Promise.all(adherencePromises);
        setPatientAdherence(adherenceData);
      } catch (error) {
        console.error('Erro ao carregar adesão:', error);
        setPatientAdherence([]);
      } finally {
        setLoading(false);
      }
    }

    if (!loadingPacientes) {
      loadAdherence();
    }
  }, [user?.uid, pacientes, loadingPacientes]);

  const filteredAndSorted = useMemo(() => {
    let filtered = patientAdherence;
    
    if (search.trim()) {
      const term = search.toLowerCase().trim();
      filtered = patientAdherence.filter(
        (p) =>
          p.nome.toLowerCase().includes(term) ||
          p.email.toLowerCase().includes(term)
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        return a.nome.localeCompare(b.nome);
      }
      if (a.adherence === null) return b.adherence === null ? a.nome.localeCompare(b.nome) : 1;
      if (b.adherence === null) return -1;
      return b.adherence - a.adherence;
    });

    return sorted;
  }, [patientAdherence, search, sortBy]);

  const metrics = useMemo(() => getAdherenceMetrics(patientAdherence), [patientAdherence]);

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Adesão aos Planos</h2>
          <p>
            Acompanhe a adesão dos seus pacientes aos planos alimentares em tempo real.
          </p>
        </div>
      </div>

      <div className="ns-grid ns-grid-3" style={{ marginBottom: '18px' }}>
        <Card title="Média geral" subtitle="Média dos pacientes com registro hoje">
          <div className="ns-kpi">
            <span className="ns-kpi-value">
              {loading ? '...' : metrics.average === null ? 'Sem dados' : `${metrics.average}%`}
            </span>
          </div>
        </Card>

        <Card title="Alta adesão" subtitle="≥ 80%">
          <div className="ns-kpi">
            <span className="ns-kpi-value" style={{ color: '#10b981' }}>
              {loading ? '...' : metrics.measuredCount === 0 ? '—' : metrics.highCount}
            </span>
          </div>
        </Card>

        <Card title="Baixa adesão" subtitle="&lt; 50%">
          <div className="ns-kpi">
            <span className="ns-kpi-value" style={{ color: 'var(--theme-danger)' }}>
              {loading ? '...' : metrics.measuredCount === 0 ? '—' : metrics.lowCount}
            </span>
          </div>
        </Card>
      </div>

      <Card
        title="Lista de pacientes"
        subtitle="Adesão atual baseada nos dados do app mobile"
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              className="ns-input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'adherence')}
              style={{ width: 'auto', padding: '6px 24px 6px 8px' }}
            >
              <option value="adherence">Ordenar por adesão</option>
              <option value="name">Ordenar por nome</option>
            </select>
          </div>
        }
      >
        <div className="ns-table-toolbar">
          <input
            className="ns-input"
            placeholder="Buscar por nome ou email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="ns-adherence-list" style={{ marginTop: '16px' }}>
          {loading && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--page-text-muted)' }}>
              Carregando adesão dos pacientes...
            </div>
          )}
          {!loading && filteredAndSorted.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--page-text-muted)' }}>
              {search.trim()
                ? 'Nenhum paciente encontrado com o termo buscado.'
                : 'Nenhum paciente com dados de adesão disponível.'}
            </div>
          )}
          {!loading &&
            filteredAndSorted.map((patient) => (
              <div key={patient.id} className="ns-adherence-item">
                <div className="ns-adherence-info">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span className="ns-adherence-name">{patient.nome}</span>
                    {patient.email && (
                      <span style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>
                        {patient.email}
                      </span>
                    )}
                  </div>
                  <span className="ns-adherence-percent">
                    {patient.adherence === null ? 'Sem registros' : `${patient.adherence}%`}
                  </span>
                </div>
                {patient.adherence !== null && (
                  <div className="ns-adherence-bar" aria-label={`Adesão ${patient.adherence}%`}>
                    <div
                      className="ns-adherence-bar-fill"
                      style={{
                        width: `${patient.adherence}%`,
                        backgroundColor:
                          patient.adherence >= 80
                            ? '#10b981'
                            : patient.adherence >= 50
                              ? '#f59e0b'
                              : '#ef4444',
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
};

















